import { utcDate } from '../format';
import type { JobStatusListOut } from '../types';

interface JobStatusViewProps {
  jobs: JobStatusListOut;
}

function humanizeSeconds(seconds: number | null): string {
  if (seconds === null) return 'never';
  if (seconds < 60) return `${Math.floor(seconds)}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function JobStatusView({ jobs }: JobStatusViewProps) {
  if (jobs.items.length === 0) {
    return (
      <div className="empty">
        <p>No background jobs are registered.</p>
      </div>
    );
  }

  return (
    <div>
      <table className="keys">
        <caption className="sr-only">Background job status and health</caption>
        <thead>
          <tr>
            <th scope="col">Job</th>
            <th scope="col">Last run</th>
            <th scope="col">Last success</th>
            <th scope="col">Failures</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {jobs.items.map((job) => (
            <tr key={job.job_name}>
              <td>
                <div className="key-name">{job.job_name}</div>
              </td>
              <td className="numeric small">
                {job.last_run_at ? (
                  <>
                    <div>{utcDate(job.last_run_at)}</div>
                    <div className="muted">{humanizeSeconds(job.age_seconds)}</div>
                  </>
                ) : (
                  <span className="muted">never</span>
                )}
              </td>
              <td className="numeric small">
                {job.last_success_at ? (
                  utcDate(job.last_success_at)
                ) : (
                  <span className="muted">never completed</span>
                )}
              </td>
              <td className="numeric">
                <span
                  className={job.consecutive_failures > 0 ? 'badge badge-warning' : 'badge badge-ok'}
                >
                  {job.consecutive_failures}
                </span>
              </td>
              <td>
                {job.stale ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className="badge badge-blocked">stale</span>
                    {job.last_error && (
                      <div className="action-error" style={{ fontSize: '11px', margin: 0 }}>
                        {job.last_error}
                      </div>
                    )}
                  </div>
                ) : job.consecutive_failures > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className="badge badge-warning">failing</span>
                    {job.last_error && (
                      <div className="action-error" style={{ fontSize: '11px', margin: 0 }}>
                        {job.last_error}
                      </div>
                    )}
                  </div>
                ) : job.last_success_at === null ? (
                  <span className="badge badge-warning">never run</span>
                ) : (
                  <span className="badge badge-ok">healthy</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
